import type { ItemAction } from 'components/DropdownContext';
import type {
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeCheckNotificationItem,
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
} from 'components/TableLikeTree/context';
import type { ITableLikeTreeFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import type { RequestStatus } from 'features/request/requestSlice';
import type {
    BimContractSeasonData,
    ConstructionTypeData,
    ContractObjectStatusEnum,
    ContractSeasonData,
    ProjectData,
    ProjectMethodologyEnum,
    WorkOrderChangeSeasonData,
    WorkOrderChangeStatusEnum,
    WorkOrderSeasonData,
    WorkOrderStatusEnum,
} from 'gql/graphql';

import * as R from 'ramda';
import type React from 'react';
import type { FormState, UseFormClearErrors, UseFormSetError, UseFormWatch } from 'react-hook-form';

import type { TConstructionTypesIndexes } from '../types';

export enum WorkOrderStructureOperation {
    CreateObject = 'CreateObject',
    DeleteSubObject = 'DeleteSubObject',
    DeleteObject = 'DeleteObject',
}
export interface IWorkOrderStructureItem {
    id: number;
    name: string;
    parentId?: number | null;
    hasPrices: boolean;
    status: ContractObjectStatusEnum;
    codeWBS?: string | null;
}

export type TWorkOrderStructureItem<T> = IWorkOrderStructureItem & T;

export interface ICrudConfigItem {
    operationSuccessCallback: (params?: any) => void;
    checkDisabledCallback?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => void;
    checkVisibleCallback?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => void;
    getTooltipCallback?: (
        treeItem: ITableLikeTreeItem,
    ) => { haveTooltip: boolean; tooltipName: string; tooltipMsg: string } | undefined;
}

export interface ICRUDConfig {
    create: ICrudConfigItem;
    update: ICrudConfigItem;
    delete: ICrudConfigItem;
    isAllMenuDisabledCallback?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => boolean;
}

export interface IUseIncludeExcludeWorkOrderObjects {
    workOrderId?: number;
    constructionTypeId?: number;
    useIncludeExcludeWorkOrderObjectsData?: (
        props: IUseIncludeExcludeWorkOrderObjectsDataProps,
    ) => IUseIncludeExcludeWorkOrderObjectsDataResult;
    updateIncludedCount: (constructionTypeId: number, includeLen: number, isReplace?: boolean) => void;
    checkedIds: number[];
    setCheckedIds: (value: React.SetStateAction<number[]>) => void;
}

export interface IUseIncludeExcludeWorkOrderObjectsResult {
    includeExcludeStatus?: RequestStatus;
    makeIncludeExcludeRequest?: (
        operation: WorkOrderStructureOperationType,
        contractObjectId?: number | undefined,
    ) => void;
}

export interface IUseWorkOrderStructureDataSourceProps<T> {
    methodology?: ProjectMethodologyEnum;
    projectData?: ProjectData | undefined | null;
    workOrderOrChangeId?: number;
    canEditObjectCode?: boolean;
    useGetIncludedCountData?: (props: IUseGetIncludedCountDataProps) => IUseGetIncludedCountDataResult<T>;
    useIncludeExcludeWorkOrderObjects: (
        props: IUseIncludeExcludeWorkOrderObjects,
    ) => IUseIncludeExcludeWorkOrderObjectsResult;
    isReadOnly?: boolean;
    isTopFilterReadOnly?: boolean;
    status?: WorkOrderStatusEnum | WorkOrderChangeStatusEnum;
}

export interface IUseWorkOrderStructureDataSourceResult<T> {
    crudMenuConfig?: ICRUDConfig;
    topFilterConfig: ITableLikeTreeFilterConfig;
    isLoading: boolean;
    isCompletePerforming: boolean;
    complete: (params: any) => void;
    items?: TWorkOrderStructureItem<T>[];
    constructionTypes?: ConstructionTypeData[];
    constructionTypeId?: number;
    seasons?:
        | (BimContractSeasonData | ContractSeasonData | WorkOrderSeasonData | WorkOrderChangeSeasonData | null)[]
        | null;
    onItemCheckChange?: (item: ITableLikeTreeItem, isChecked: boolean) => void;
    onCheckAll?: (isChecked: boolean) => void;
    areCheckboxesDisabled?: boolean;
    disabledCheckBoxesItemsIds?: number[];
    dragOperationDisabledItemsIds?: number[];
    checkConfirmations?: ITableLikeTreeCheckConfirmation[];
    checkBoxesNotifications?: ITableLikeTreeCheckNotificationItem[];
    checkedIds?: number[];
    selectedSeasonsIds?: number[];
    informers?: ITableLikeTreeItemNotification[];
    setConstructionTypeId: (value: React.SetStateAction<number | undefined>) => void;
    includedCount?: Record<TConstructionTypesIndexes, number>;
    isHeaderCheckboxDisabled?: boolean;
    getStructureOperationConfirmationMessage?: (
        operation?: WorkOrderStructureOperation,
        item?: TWorkOrderStructureItem<T>,
    ) => IWorkOrderStructureOperationConfirmationMessage | undefined;
}
export interface IUseWorkOrderEditObjectDataSourceProps<T> {
    formState?: FormState<any>;
    setError?: UseFormSetError<any>;
    clearErrors?: UseFormClearErrors<any>;
    watch?: UseFormWatch<any>;
    constructionTypeId?: number;
    contractId?: number;
    workOrderOrChangeId?: number;
    onCreateSuccess?: (newObjectId?: number) => void;
    onUpdateSuccess?: (objectId?: number) => void;
    objectId?: number;
    initialCode?: string | null;
    isContractObject?: boolean;
    parentObjectToAppend?: TWorkOrderStructureItem<T>;
    checkedIds?: number[];
}
export interface IUseWorkOrderEditObjectDataSourceResult {
    doCreateRequest: (params?: any) => void;
    doUpdateRequest: (params?: any) => void;
    btnConfirmDisabled?: boolean;
}

export interface IUseRemoveWorkOrderObjectProps {
    workOrderOrChangeId?: number;
    onSuccess?: () => void;
}

export interface IUseRemoveWorkOrderObjectResult {
    status: RequestStatus;
    makeRequest: (objectId: number) => void;
}
export interface IUseGetIncludedCountDataProps {
    workOrderOrChangeId?: number;
}

export interface IUseGetIncludedCountDataResult<T> {
    constructionTypeIdGetMap?: string[];
    includedFlagGetMap?: string[];
    status: RequestStatus;
    makeRequest: (constructionTypeId?: number) => void;
    reset: () => void;
    items?: T[];
}

export enum WorkOrderStructureOperationType {
    Include = 'Include',
    Exclude = 'Exclude',
    IncludeAll = 'IncludeAll',
    ExcludeAll = 'ExcludeAll',
}

export interface IUseIncludeExcludeWorkOrderObjectsDataProps {
    onSuccess: (operation: WorkOrderStructureOperationType, result: number[]) => void;
    workOrderOrChangeId?: number;
    constructionTypeId?: number;
}

export interface IUseIncludeExcludeWorkOrderObjectsDataResult {
    makeRequest: (operation: WorkOrderStructureOperationType, contractObjectId?: number) => void;
    status: RequestStatus;
}

export interface IWorkOrderObjectEdit<T> {
    contractId?: number;
    workOrderOrChangeId?: number;
    canEditObjectCode?: boolean;
    constructionTypeId?: number;
    seasons?:
        | (BimContractSeasonData | ContractSeasonData | WorkOrderSeasonData | WorkOrderChangeSeasonData | null)[]
        | null;
    onClose: (isSuccess?: boolean, newSubObjectId?: number) => void;
    objectToEdit?: TWorkOrderStructureItem<T>;
    useWorkOrderEditObjectDataSource?: (
        props: IUseWorkOrderEditObjectDataSourceProps<T>,
    ) => IUseWorkOrderEditObjectDataSourceResult;
    parentObjectToAppend?: TWorkOrderStructureItem<T>;
    checkedIds?: number[];
}

export interface IWorkOrderStructurePageLayout<T> {
    workOrderOrChangeId?: number;
    canEditObjectCode?: boolean;
    menuItemsGetter?: ((treeItem: ITableLikeTreeItem) => ItemAction[]) | undefined;
    isObjectsFormationStage?: boolean;
    isFrameContract?: boolean;
    methodology?: ProjectMethodologyEnum;
    projectData?: ProjectData | null;
    useGetDataSource: (props: IUseWorkOrderStructureDataSourceProps<T>) => IUseWorkOrderStructureDataSourceResult<T>;
    WorkOrderObjectEditComponent: React.FC<IWorkOrderObjectEdit<T>>;
    WorkOrderSubObjectEditComponent: React.FC<IWorkOrderObjectEdit<T>>;
    isReadonly?: boolean;
    workOrderFullNumber?: string;
    useWorkOrderEditObjectDataSource?: (
        props: IUseWorkOrderEditObjectDataSourceProps<T>,
    ) => IUseWorkOrderEditObjectDataSourceResult;
    useRemoveWorkOrderObject?: (props: IUseRemoveWorkOrderObjectProps) => IUseRemoveWorkOrderObjectResult;
    useGetIncludedCountData?: (props: IUseGetIncludedCountDataProps) => IUseGetIncludedCountDataResult<T>;
    skipEntityNameInTitle?: boolean;
    isTopFilterReadOnly?: boolean;
    status?: WorkOrderStatusEnum | WorkOrderChangeStatusEnum;
}

export interface IWorkOrderStructure<T> {
    items?: IWorkOrderStructureItem[];
    crudMenuConfig?: ICRUDConfig;
    topFilterConfig: ITableLikeTreeFilterConfig;
    menuItemsGetter?: ((treeItem: ITableLikeTreeItem, isChecked?: boolean) => ItemAction[]) | undefined;
    informerMessage?: string;
    constructionTypes?: ConstructionTypeData[];
    constructionTypeId?: number;
    seasons?:
        | (BimContractSeasonData | ContractSeasonData | WorkOrderSeasonData | WorkOrderChangeSeasonData | null)[]
        | null;
    WorkOrderObjectEditComponent: React.FC<IWorkOrderObjectEdit<T>>;
    WorkOrderSubObjectEditComponent: React.FC<IWorkOrderObjectEdit<T>>;
    workOrderOrChangeId?: number;
    canEditObjectCode?: boolean;
    contractId?: number;
    onItemCheckChange?: (item: ITableLikeTreeItem, isChecked: boolean) => void;
    areCheckboxesDisabled?: boolean;
    onCheckAll?: (isChecked: boolean) => void;
    disabledCheckBoxesItemsIds?: number[];
    dragOperationDisabledItemsIds?: number[];
    checkConfirmations?: ITableLikeTreeCheckConfirmation[];
    checkBoxesNotifications?: ITableLikeTreeCheckNotificationItem[];
    checkedIds?: number[];
    selectedSeasonsIds?: number[];
    informers?: ITableLikeTreeItemNotification[];
    onLeftFilterRowSelected?: (itemId: number) => void;
    isReadOnly?: boolean;
    useWorkOrderEditObjectDataSource?: (
        props: IUseWorkOrderEditObjectDataSourceProps<T>,
    ) => IUseWorkOrderEditObjectDataSourceResult;
    useRemoveWorkOrderObject?: (props: IUseRemoveWorkOrderObjectProps) => IUseRemoveWorkOrderObjectResult;
    includedCount?: Record<TConstructionTypesIndexes, number>;
    isHeaderCheckboxDisabled?: boolean;
    getStructureOperationConfirmationMessage?: (
        operation?: WorkOrderStructureOperation,
        item?: TWorkOrderStructureItem<T>,
    ) => IWorkOrderStructureOperationConfirmationMessage | undefined;
}

export const getParentProp = (item: IWorkOrderStructureItem) => (item.parentId ? String(item.parentId) : 'root');
export const groupByParentId = R.groupBy(getParentProp);
export const sortByParentBaseOrder = R.sortBy(R.prop('order'));

const createObjectConfirmMessage =
    'Создание объекта приведет к обновлению структуры договора. Вы уверены, что хотите продолжить?';
const deleteObjectConfirmMessage = 'Вы уверены, что хотите удалить объект? Все дочерние элементы будут удалены';
const deleteSubObjectConfirmMessage = 'Вы уверены, что хотите удалить подобъект?';

export const deleteTitle = 'Удалить';
export const doNotDeleteTitle = 'Не удалять';

export interface IWorkOrderStructureOperationConfirmationMessage {
    textConfirmation: string;
    textHeader: string;
    cancelButtonText: string;
    confirmButtonText: string;
}

export const structureOperationConfirmationMessages: Record<
    WorkOrderStructureOperation,
    IWorkOrderStructureOperationConfirmationMessage
> = {
    [WorkOrderStructureOperation.CreateObject]: {
        textConfirmation: createObjectConfirmMessage,
        textHeader: 'Создание объекта',
        cancelButtonText: 'Отменить',
        confirmButtonText: 'Продолжить',
    },
    [WorkOrderStructureOperation.DeleteSubObject]: {
        textConfirmation: deleteSubObjectConfirmMessage,
        textHeader: 'Удаление подобъекта',
        cancelButtonText: doNotDeleteTitle,
        confirmButtonText: deleteTitle,
    },
    [WorkOrderStructureOperation.DeleteObject]: {
        textConfirmation: deleteObjectConfirmMessage,
        textHeader: 'Удаление объекта',
        cancelButtonText: doNotDeleteTitle,
        confirmButtonText: deleteTitle,
    },
};
